import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG } from '@boma/app-config';
import { IAssignedAdminRoles, IPaginatedResponse } from '@boma/client-shared';
import {
  IAdministrator,
  ILocation,
  IVehicle,
} from '@boma/supplier-portal/shared/entities';
import { LocationType } from '@boma/website/shared/entities';
import { Observable, forkJoin, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { HelperFunctionsService } from '../helper-functions/helper-functions.service';

@Injectable({
  providedIn: 'root',
})
export class LocationsService {
  private base: string;
  private version: string;

  private loadedQueries = []; // lets tract the page number and query that was searched for so we don't query it again.
  private cachedLocations: ILocation | IVehicle | any = [];
  private loadedAllLocations = false;
  private currentEntity = '';
  private totalItems = null;

  private lastPagination: IPaginatedResponse<ILocation> = {
    items: [],
    nextPage: 0,
    page: 0,
    previousPage: 0,
    totalItems: this.totalItems,
    totalPages: 0,
  };

  constructor(
    @Inject(APP_CONFIG) private configuration: any,
    private httpClient: HttpClient,
    private helperService: HelperFunctionsService
  ) {
    this.base = configuration.apiRoute;
    this.version = configuration.apiVersion;
  }

  // Getter for loadedAllLocations
  // get isLoadedAllLocations(): boolean {
  //   // We dont ever change this yet so this will always be false
  //   return this.loadedAllLocations;
  // }

  isLoadedAllLocations(entityId: string): boolean {
    if (entityId !== this.currentEntity) this.clearAll();
    return this.loadedAllLocations;
  }

  get getLoadedLocations(): IPaginatedResponse<ILocation> {
    return this.lastPagination;
  }

  loadLocations(
    entityId: string,
    page: string = '1',
    perPage: string = '25',
    query: string = '',
    type: string = ''
  ) {
    return this.httpClient.get<IPaginatedResponse<ILocation>>(
      `${this.base}api/v${this.version}/${entityId}/Locations`,
      {
        params: { page, perPage, query, type },
      }
    );
  }

  // TODO could remove this no as locations takes nullable type value that will load everything
  // This logic is a massive pain
  loadVehiclesAndLocations(
    entityId: string,
    page: string = '1',
    perPage: string = '25',
    query: string = ''
  ): Observable<{ locations: ILocation[]; vehicles: IVehicle[] }> {
    let locations = this.httpClient.get<IPaginatedResponse<ILocation>>(
      `${this.base}api/v${this.version}/${entityId}/Locations`,
      {
        params: { page, perPage, query },
      }
    );

    let vehicles = this.httpClient.get<IPaginatedResponse<IVehicle>>(
      `${this.base}api/v${this.version}/${entityId}/Vehicles`,
      {
        params: { page, perPage, query },
      }
    );

    return forkJoin([locations, vehicles]).pipe(
      map((x) => ({
        locations: x[0].items.map((x) => {
          x.type = 'location';
          return x;
        }),
        vehicles: x[1].items.map((x) => {
          x.type = 'vehicle';
          return x;
        }),
      }))
    );
  }

  loadLocation(entityId: string, id: string) {
    return this.httpClient.get<ILocation>(
      `${this.base}api/v${this.version}/${entityId}/Locations/${id}`
    );
  }

  clearAll() {
    this.loadedQueries = []; // lets tract the page number and query that was searched for so we don't query it again.
    this.cachedLocations = [];
    this.loadedAllLocations = false;
    this.currentEntity = '';
    this.totalItems = null;

    this.lastPagination = {
      items: [],
      nextPage: 0,
      page: 0,
      previousPage: 0,
      totalItems: this.totalItems,
      totalPages: 0,
    };
  }

  //Loop the searched until we have received all the locations
  loadAllLocations(entityId) {
    if (this.currentEntity !== entityId) {
      this.clearAll();
    }

    const totalPages = this.lastPagination?.totalPages || 0; // If zero we clearly have no locations set up or something is wrong

    const requests = [];
    // Loop through each page and create an array of requests
    for (let page = 1; page <= totalPages; page++) {
      const request = this.searchLocations(
        entityId,
        '',
        null,
        page,
        this.lastPagination?.totalItems || 25
      );
      requests.push(request);
    }

    // Wait for all requests to complete
    return forkJoin(requests).pipe(
      map(() => {
        this.loadedAllLocations = true;
        return this.lastPagination; // Return the cached locations once all requests are done
      })
    );
  }

  searchLocations(
    entityId: string,
    query: string = '',
    type?: LocationType,
    page: number = 1,
    perPage: number = 25
  ) {
    if (!this.currentEntity) this.currentEntity = entityId;
    const queryParams = [];

    type && queryParams.push(`type=${type}`);
    queryParams.push(`page=${page}`);
    queryParams.push(`perPage=${perPage}`);
    query && queryParams.push(`query=${query}`);

    const urlQuery = `?${queryParams.join('&')}`;

    if (entityId !== this.currentEntity) this.cachedLocations = []; // entity changed dump cache
    const newQuery = this.loadedQueries.indexOf(urlQuery) === -1; // have we searched this before?
    if (newQuery) this.loadedQueries.push(urlQuery);

    //CHECK IF WE HAVE LOADED THIS QUERY BEFORE OR IF WE HAVE LOADED ALL LOCATIONS AND ITS NOT A REFRESH CALL

    if (this.cachedLocations.length && !newQuery) {
      console.log('returning cached');
      // If locations are already cached, return them directly
      //This pagination obviously needs to be better... we could store it based on the query and then just return all the locations
      return of(this.lastPagination);
    } else {
      return this.httpClient
        .get<IPaginatedResponse<ILocation>>(
          `${this.base}api/v${this.version}/${entityId}/Locations${urlQuery}`
        )
        .pipe(
          map((response) => {
            if (query === '' && !this.totalItems)
              this.totalItems = response.totalItems;
            const newLocations = response.items;

            this.lastPagination = response;

            const uniqueItems = newLocations.filter((newItem) => {
              return !this.cachedLocations.some((existingItem) => {
                return existingItem.id === newItem?.id;
              });
            });
            this.cachedLocations = [...this.cachedLocations, ...uniqueItems];

            if (query === '' && this.cachedLocations.length === this.totalItems)
              this.loadedAllLocations = true;
            this.lastPagination.items = this.cachedLocations;
            return response;
          })
        );
    }
  }

  updateLocationPermissions(
    entityId: string,
    locationId: string,
    userId: string,
    roleIds: string[]
  ) {
    return this.httpClient.put<IAssignedAdminRoles>(
      `${this.base}api/v${this.version}/${entityId}/Locations/${locationId}/Administrators/${userId}`,
      {
        roleIds,
      }
    );
  }

  createLocation(entityId: string, location: ILocation) {
    return this.httpClient.post<ILocation>(
      `${this.base}api/v${this.version}/${entityId}/Locations`,
      {
        ...location,
      }
    );
  }

  updateLocation(entityId: string, locationId: string, location: ILocation) {
    return this.httpClient.put<ILocation>(
      `${this.base}api/v${this.version}/${entityId}/Locations/${locationId}`,
      {
        ...location,
      }
    );
  }

  listAdministrators(entityId: string, locationId: string, query: string = '') {
    return this.httpClient.get<IAdministrator[]>(
      `${this.base}api/v${this.version}/${entityId}/Locations/${locationId}/Administrators`,
      {
        params: { query },
      }
    );
  }

  createAdministrator(
    entityId: string,
    locationId: string,
    emailAddressOrPhoneNumber: string,
    roleIds: string[]
  ) {
    return this.httpClient.post<IAdministrator>(
      `${this.base}api/v${this.version}/${entityId}/Locations/${locationId}/Administrators`,
      {
        emailAddressOrPhoneNumber,
        roleIds,
      }
    );
  }

  loadAdministratorDetails(
    entityId: string,
    locationId: string,
    userId: string
  ) {
    return this.httpClient.get<IAdministrator>(
      `${this.base}api/v${this.version}/${entityId}/Locations/${locationId}/Administrators/${userId}`
    );
  }

  deleteAdministrator(entityId: string, locationId: string, userId: string) {
    return this.httpClient.delete(
      `${this.base}api/v${this.version}/${entityId}/Locations/${locationId}/Administrators/${userId}`
    );
  }

  updateAdministrator(
    entityId: string,
    locationId: string,
    userId: string,
    roleIds: string[]
  ) {
    return this.httpClient.put<IAdministrator>(
      `${this.base}api/v${this.version}/${entityId}/Locations/${locationId}/Administrators/${userId}`,
      {
        roleIds,
      }
    );
  }
}
